(function(global){
  'use strict';
  const clamp=(value,min,max)=>Math.min(max,Math.max(min,value));
  const finite=value=>typeof value==='number'&&Number.isFinite(value);
  const hex=rgb=>'#'+rgb.map(value=>clamp(Math.round(value),0,255).toString(16).padStart(2,'0')).join('');
  function rgb(color){
    if(typeof color!=='string'||!/^#[0-9a-f]{6}$/i.test(color))return null;
    return [1,3,5].map(index=>parseInt(color.slice(index,index+2),16));
  }
  function hsl(channels){
    const [r,g,b]=channels.map(value=>value/255),max=Math.max(r,g,b),min=Math.min(r,g,b),light=(max+min)/2,delta=max-min;
    if(!delta)return {h:30,s:0,l:light};
    const saturation=delta/(1-Math.abs(2*light-1));
    const hue=max===r?((g-b)/delta)%6:max===g?(b-r)/delta+2:(r-g)/delta+4;
    return {h:(hue*60+360)%360,s:saturation,l:light};
  }
  function hslHex(h,s,l){
    h=(h%360+360)%360;s=clamp(s,0,1);l=clamp(l,0,1);
    const chroma=(1-Math.abs(2*l-1))*s,x=chroma*(1-Math.abs((h/60)%2-1)),m=l-chroma/2;
    const channels=h<60?[chroma,x,0]:h<120?[x,chroma,0]:h<180?[0,chroma,x]:h<240?[0,x,chroma]:h<300?[x,0,chroma]:[chroma,0,x];
    return hex(channels.map(value=>(value+m)*255));
  }
  const colorDistance=(a,b)=>Math.hypot(a[0]-b[0],a[1]-b[1],a[2]-b[2]);
  function luminance(channels){
    const linear=channels.map(value=>{value/=255;return value<=.04045?value/12.92:((value+.055)/1.055)**2.4;});
    return .2126*linear[0]+.7152*linear[1]+.0722*linear[2];
  }
  function sampleJitter(index){
    let value=Math.imul(index+1,0x45d9f3b);value=Math.imul(value^(value>>>16),0x45d9f3b);
    return ((value^(value>>>16))>>>0)/4294967296;
  }
  function analyzeColors(imageData,{maxSamples=12000,maxColors=5}={}){
    const width=imageData?.width,height=imageData?.height,data=imageData?.data;
    if(!Number.isSafeInteger(width)||!Number.isSafeInteger(height)||width<1||height<1||width*height>100e6||
      !(data instanceof Uint8Array||data instanceof Uint8ClampedArray)||data.length!==width*height*4)
      throw new Error('invalid_image_data');
    if(!Number.isSafeInteger(maxSamples)||maxSamples<1||maxSamples>60000||!Number.isSafeInteger(maxColors)||maxColors<1||maxColors>8)
      throw new Error('invalid_analysis_options');
    const total=width*height,sampledPixels=Math.min(total,maxSamples),buckets=new Map();let acceptedPixels=0;
    for(let sample=0;sample<sampledPixels;sample++){
      const index=total===sampledPixels?sample:Math.floor((sample+sampleJitter(sample))*total/sampledPixels),offset=index*4;
      if(data[offset+3]<230)continue;
      const channels=[data[offset],data[offset+1],data[offset+2]],light=luminance(channels);
      if(light<.015||light>.96)continue;
      acceptedPixels++;
      const key=channels.map(value=>Math.floor(value/32)).join(',');
      let bucket=buckets.get(key);
      if(!bucket){bucket={key,count:0,sum:[0,0,0]};buckets.set(key,bucket);}
      bucket.count++;channels.forEach((value,i)=>{bucket.sum[i]+=value;});
    }
    const ordered=[...buckets.values()].map(bucket=>({...bucket,color:bucket.sum.map(value=>value/bucket.count)}))
      .sort((a,b)=>b.count-a.count||a.key.localeCompare(b.key,'en'));
    const clusters=[];
    for(const bucket of ordered){
      let nearest=null,distance=42;
      for(const cluster of clusters){const current=colorDistance(cluster.color,bucket.color);if(current<distance){distance=current;nearest=cluster;}}
      if(nearest){nearest.count+=bucket.count;nearest.sum=nearest.sum.map((value,i)=>value+bucket.sum[i]);nearest.color=nearest.sum.map(value=>value/nearest.count);}
      else clusters.push({count:bucket.count,sum:bucket.sum.slice(),color:bucket.color.slice()});
    }
    const significant=clusters.filter(cluster=>cluster.count>=Math.max(1,acceptedPixels*.004))
      .sort((a,b)=>b.count-a.count||hex(a.color).localeCompare(hex(b.color),'en')).slice(0,maxColors);
    const palette=significant.map(cluster=>({color:hex(cluster.color),weight:Number((cluster.count/acceptedPixels).toFixed(6))}));
    return {method:'local_pixels',palette,dominant:palette[0]?.color||null,sampledPixels,acceptedPixels,coverage:Number((acceptedPixels/sampledPixels).toFixed(6))};
  }
  function recommendDesigns(analysis){
    const palette=Array.isArray(analysis?.palette)&&(!finite(analysis.coverage)||analysis.coverage>=.04)?analysis.palette.filter(item=>rgb(item?.color)&&finite(item.weight)&&item.weight>0):[];
    const source=palette.length?'local_pixels':'neutral_fallback';
    const dominant=palette.length?rgb(palette[0].color):[222,211,193],base=hsl(dominant);
    const candidates=palette.map(item=>({channels:rgb(item.color),weight:item.weight}));
    candidates.sort((a,b)=>{
      const score=item=>item.weight*(hsl(item.channels).s+.12)*(colorDistance(item.channels,dominant)>36?1.5:1);
      return score(b)-score(a)||hex(a.channels).localeCompare(hex(b.channels),'en');
    });
    const anchor=hsl(candidates[0]?.channels||dominant),baseHue=base.s>.055?base.h:30,anchorHue=anchor.s>.065?anchor.h:baseHue;
    return [
      {id:'harmony',motif:'arches',colors:[hslHex(baseHue,clamp(base.s*.22,.035,.13),.9),hslHex(anchorHue,clamp(anchor.s*.55+.08,.12,.42),.32),hslHex(anchorHue,clamp(anchor.s*.38+.08,.12,.34),.64)],source},
      {id:'light',motif:'horizon',colors:[hslHex(baseHue,.045,.95),hslHex(anchorHue,clamp(anchor.s*.25,.06,.2),.68),hslHex(baseHue,clamp(base.s*.22,.045,.16),.8)],source},
      {id:'contrast',motif:'lignes',colors:[hslHex(baseHue,.09,.9),hslHex(anchorHue+180,.16,.18),hslHex(anchorHue,clamp(anchor.s*.58+.12,.24,.48),.53)],source}
    ];
  }
  function validWall(quad){
    if(!Array.isArray(quad)||quad.length!==4||!quad.every(point=>point&&finite(point.x)&&finite(point.y)&&point.x>=0&&point.x<=1&&point.y>=0&&point.y<=1))return false;
    const crosses=quad.map((point,i)=>{
      const a=quad[(i+1)%4],b=quad[(i+2)%4];return (a.x-point.x)*(b.y-a.y)-(a.y-point.y)*(b.x-a.x);
    });
    return crosses.every(value=>value>1e-7);
  }
  function homography(quad){
    const [p0,p1,p2,p3]=quad,dx1=p1.x-p2.x,dx2=p3.x-p2.x,dx3=p0.x-p1.x+p2.x-p3.x;
    const dy1=p1.y-p2.y,dy2=p3.y-p2.y,dy3=p0.y-p1.y+p2.y-p3.y,det=dx1*dy2-dx2*dy1;
    let g=0,h=0;
    if(Math.abs(dx3)+Math.abs(dy3)>1e-12){if(Math.abs(det)<1e-12)return null;g=(dx3*dy2-dx2*dy3)/det;h=(dx1*dy3-dx3*dy1)/det;}
    if([1,1+g,1+h,1+g+h].some(value=>!finite(value)||value<=1e-9))return null;
    return [p1.x-p0.x+g*p1.x,p3.x-p0.x+h*p3.x,p0.x,p1.y-p0.y+g*p1.y,p3.y-p0.y+h*p3.y,p0.y,g,h];
  }
  function project(matrix,u,v){
    const denominator=matrix[6]*u+matrix[7]*v+1;
    return {x:(matrix[0]*u+matrix[1]*v+matrix[2])/denominator,y:(matrix[3]*u+matrix[4]*v+matrix[5])/denominator};
  }
  function projectToWall({wallQuad,wallWidthCm,wallHeightCm,artworkWidthCm,artworkHeightCm,centerX=.5,centerY=.5,calibrated=true,photoAspect=1.5,approximateWidth=.55}={}){
    const failure=reason=>({ok:false,reason});
    if(!validWall(wallQuad))return failure('invalid_wall');
    if(!finite(artworkWidthCm)||!finite(artworkHeightCm)||artworkWidthCm<=0||artworkHeightCm<=0)return failure('invalid_dimensions');
    if(!finite(centerX)||!finite(centerY))return failure('invalid_center');
    if(typeof calibrated!=='boolean')return failure('invalid_calibration');
    let width,height;
    if(calibrated){
      if(!finite(wallWidthCm)||!finite(wallHeightCm)||wallWidthCm<=0||wallHeightCm<=0)return failure('invalid_dimensions');
      if(artworkWidthCm>wallWidthCm||artworkHeightCm>wallHeightCm)return failure('artwork_too_large');
      width=artworkWidthCm/wallWidthCm;height=artworkHeightCm/wallHeightCm;
    }else{
      if(!finite(photoAspect)||photoAspect<=0||!finite(approximateWidth)||approximateWidth<=0)return failure('invalid_dimensions');
      const distance=(a,b)=>Math.hypot((a.x-b.x)*photoAspect,a.y-b.y);
      const apparentWidth=(distance(wallQuad[0],wallQuad[1])+distance(wallQuad[3],wallQuad[2]))/2;
      const apparentHeight=(distance(wallQuad[0],wallQuad[3])+distance(wallQuad[1],wallQuad[2]))/2;
      width=clamp(approximateWidth,.025,.95);height=width*(apparentWidth/apparentHeight)/(artworkWidthCm/artworkHeightCm);
      if(height>.95){width*=.95/height;height=.95;}
    }
    const center={x:clamp(centerX,width/2,1-width/2),y:clamp(centerY,height/2,1-height/2)};
    const wallRect={x:center.x-width/2,y:center.y-height/2,width,height};
    const matrix=homography(wallQuad);if(!matrix)return failure('invalid_wall');
    const quad=[[wallRect.x,wallRect.y],[wallRect.x+width,wallRect.y],[wallRect.x+width,wallRect.y+height],[wallRect.x,wallRect.y+height]].map(([u,v])=>project(matrix,u,v));
    if(!quad.every(point=>finite(point.x)&&finite(point.y)&&point.x>=-1e-8&&point.x<=1+1e-8&&point.y>=-1e-8&&point.y<=1+1e-8)||!validWall(quad))return failure('invalid_projection');
    return {ok:true,quad,wallRect,center,calibrated};
  }
  const api={analyzeColors,recommendDesigns,projectToWall,validWall};
  global.SourdineRoomAdvisor=api;
  if(typeof module!=='undefined'&&module.exports)module.exports=api;
})(typeof window!=='undefined'?window:globalThis);
